import { IFloorPlanModel } from '@api/models';
import { ZONE_COLORS } from 'modules/site-walk-gui/helper/constants';
import { Polygon } from 'modules/site-walk-gui/helper/types';
import useFloorPlanListData from 'modules/site-walk/hooks/useFloorPlanListData';
import { useParams } from 'react-router-dom';

interface PropertiesPanelProps {
    polygon: Polygon;
    onChangeColor: (color: string) => void;
    onChangeFloorPlan: (floorPlan: string) => void;
    floorPlans: IFloorPlanModel[];
    floorPlanLoading: boolean;
}

export const PropertiesPanel = ({
    polygon,
    onChangeColor,
    onChangeFloorPlan,
    floorPlans,
    floorPlanLoading: isLoading,
}: PropertiesPanelProps) => {
    const floorPlanMissing = polygon.floorPlan.trim() === '';

    return (
        <div className="w-full p-4 flex flex-col gap-4">
            <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Properties</span>

            {/* Floor Plan — value is the floor plan _id, label shown is the name */}
            <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500 flex items-center gap-1">
                    Floor Plan
                    {floorPlanMissing && <span className="text-amber-400 text-[10px]">required</span>}
                </label>
                <select
                    value={polygon.floorPlan}
                    disabled={isLoading}
                    onChange={(e) => onChangeFloorPlan(e.target.value)}
                    className={`bg-[#0f172a] text-white text-sm px-2 py-1.5 rounded border outline-none transition-colors cursor-pointer disabled:opacity-50 ${
                        floorPlanMissing
                            ? 'border-amber-500/60 focus:border-amber-400'
                            : 'border-white/10 focus:border-blue-500'
                    }`}
                >
                    <option value="" disabled>
                        {isLoading ? 'Loading…' : 'Select floor plan…'}
                    </option>
                    {floorPlans?.map((fp) => (
                        <option key={fp._id} value={fp._id}>
                            {fp._id}-{fp.name}
                        </option>
                    ))}
                </select>
            </div>

            <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500">Color</label>
                <div className="flex flex-wrap gap-1.5">
                    {ZONE_COLORS.map((color) => (
                        <button
                            key={color}
                            onClick={() => onChangeColor(color)}
                            className="w-6 h-6 rounded transition-transform hover:scale-110"
                            style={{
                                backgroundColor: color,
                                outline: polygon.color === color ? '2px solid white' : 'none',
                                outlineOffset: 2,
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-1">
                <label className="text-xs text-slate-500">Vertices</label>
                <span className="text-sm text-slate-300">{polygon.points.length} points</span>
            </div>
        </div>
    );
};
